import { useEffect, useState } from "react";
import axios, { apiBaseUrl } from "../../api/axios";
import ResponsiveTable from "../../components/ResponsiveTable";
import Loader from "../../components/Loader";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import ExportButtons from "../../components/ExportButtons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";

export default function Colors() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [colorId, setColorId] = useState(null);

  // form fields
  const [name, setName] = useState("");
  const [hexCode, setHexCode] = useState("#000000"); // optional color picker

  const closeModal = () => {
    setName("");
    setHexCode("#000000");
    setColorId(null);
    setIsModalOpen(false);
  };

  const [colorsList, setColorsList] = useState([]);

  const getSingleColor = (id) => {
    axios.get(`/meta/colors/${id}`).then((res) => {
      setName(res.data?.name || "");
      setHexCode(res.data?.hex_code || "#000000");
    });
  };

  const fetchColors = () => {
    axios.get("/meta/colors").then((res) => {
      setColorsList(res.data || []);
    });
  };

  useEffect(() => {
    fetchColors();
  }, []);

  const handleSubmit = () => {
    setLoading(true);
    setIsModalOpen(false);

    const payload = {
      name: name?.trim(),
      // send hex_code only if you added the column in DB
      hex_code: hexCode || null,
    };

    const apiUrl = colorId !== null ? `/meta/colors/${colorId}` : "/meta/colors";
    const apiCall = colorId ? axios.put(apiUrl, payload) : axios.post(apiUrl, payload);

    apiCall
      .then((res) => {
        toast.success(
          res.data?.message || `Color ${colorId !== null ? "updated" : "created"} successfully!`
        );
        setLoading(false);
        fetchColors();
        navigate("/item/colors");
        setName("");
        setHexCode("#000000");
        setColorId(null);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.response?.data?.error || err.message || "Error");
      });
  };

  const actions = [
    { label: "Edit", value: "edit", icon: <PencilIcon className="w-4 h-4 inline" /> },
    { divider: true },
    { label: "Delete", value: "delete", icon: <TrashIcon className="w-4 h-4 inline text-red-600" /> },
  ];

  const handleActionClick = (action, row) => {
    switch (action) {
      case "edit":
        setColorId(row.id);
        getSingleColor(row.id);
        setIsModalOpen(true);
        break;
      case "delete":
        if (window.confirm("Are you sure you want to delete this Color?")) {
          axios.delete(`/meta/colors/${row.id}`).then(() => {
            fetchColors();
          });
        }
        break;
      default:
        break;
    }
  };

  const handleDownload = (type) => {
    // Replace with a dedicated export if you add one for colors.
    const url = `${apiBaseUrl}/purchases/export/${type}`;
    window.open(url, "_blank");
  };

  const columns = [
    { header: "SL", accessor: "sl", render: (v, row, i) => i + 1 },
    { header: "Color Name", accessor: "name" },
    {
      header: "Swatch",
      accessor: "hex_code",
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <span
            title={row.hex_code || "#000000"}
            style={{ backgroundColor: row.hex_code || "#000000" }}
            className="inline-block w-5 h-5 rounded border border-gray-300"
          />
          <span className="text-xs text-gray-600">{row.hex_code || "-"}</span>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 max-w-6xl mx-auto text-sm bg-white rounded-md">
      <div className="w-full">
        <div className="flex justify-end">
          <button
            onClick={() => setIsModalOpen(true)}
            className=" bg-purple-600 text-white px-4 py-1 rounded text-sm"
          >
            + Add New
          </button>
        </div>
        <ResponsiveTable
          columns={columns}
          data={colorsList}
          showActions
          actionButtons={actions}
          onActionClick={handleActionClick}
        />
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        modalWidth="max-w-md"
        header={<span className="text-blue-600">Add/Update Color</span>}
        body={
          <div className="space-y-3">
            <div>
              <label className="font-semibold text-sm">Name</label>
              <input
                type="text"
                value={name}
                placeholder="Enter color name (e.g., Red)"
                onChange={(e) => setName(e.target.value)}
                className="border w-full rounded mt-1 p-2"
              />
            </div>

            <div>
              <label className="font-semibold text-sm">Pick Color</label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={hexCode}
                  onChange={(e) => setHexCode(e.target.value)}
                  className="h-9 w-12 p-0 border rounded"
                  title="Pick hex color"
                />
                <input
                  type="text"
                  value={hexCode}
                  onChange={(e) => setHexCode(e.target.value)}
                  placeholder="#000000"
                  className="border rounded p-2 flex-1"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Optional, stored in <code>colors.hex_code</code>. Add the column if you haven’t.
              </p>
            </div>
          </div>
        }
        footer={
          <div className="flex gap-2">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
            >
              Close
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 text-white bg-green-700 rounded hover:bg-green-800"
            >
              Save
            </button>
          </div>
        }
      />

      <Loader show={loading} />
    </div>
  );
}